/**
 * A mailbox that no longer exists (spec 2026-10-29-mailbox-gone-design.md).
 *
 * When a provider says, definitively, that a person's mailbox does not exist, the API sets that person
 * Inactive and records WHY on the recipient. Every recipient response that carries `status` also carries
 *
 *   mailboxGone: { at, provider, source, code } | null
 *
 * which this module types at the boundary (a declared type that contradicts the API is invisible to tsc,
 * CLAUDE.md), words for the screen, and names the list filter for. Nothing here reads the provider's text:
 * `code` is a short machine code ('google:invalid_grant:invalid_user', 'graph:ErrorInvalidUser',
 * 'smtp:5.1.1') kept for support, never shown as a sentence.
 *
 * The vocabularies mirror apps/api/src/common/mail/mailbox-gone.types.ts. They are read OFF THE WIRE, so a
 * member a newer API adds must not crash a screen: every display map is indexed through a function that
 * falls back (compare PayloadType, CLAUDE.md 2026-09-07). The guard
 * `lib/recipients/__tests__/mailbox-gone-vocabulary.guard.spec.ts` parses the API file and fails when a
 * member has no words here.
 */
import { z } from 'zod'

/** Where the provider said the mailbox is gone. */
export type MailboxGoneProvider = 'google' | 'microsoft' | 'smtp'

/** Which feature found out. */
export type MailboxGoneSource =
  | 'connection_test'
  | 'dwd_check'
  | 'snr'
  | 'er_fetch'
  | 'gmail_watch'
  | 'esa'
  | 'delivery'
  | 'bounce'

/** The mark on a recipient. `at` is an ISO instant ending in `Z`: shown in the viewer's zone. */
export interface MailboxGone {
  at: string
  provider: MailboxGoneProvider | (string & {})
  source: MailboxGoneSource | (string & {})
  code: string
}

const mailboxGoneObjectSchema = z.object({
  at: z.string().min(1),
  // Strings, not enums: a member the API adds later must still parse, and the screen words it generically.
  provider: z.string().min(1),
  source: z.string().min(1),
  code: z.string().default(''),
})

/** The boundary schema: the mark, or null (the person is not marked, or the API did not send the field). */
export const mailboxGoneSchema = mailboxGoneObjectSchema.nullable().catch(null)

/** Read `recipient.mailboxGone` off the wire. Anything that is not a mark is `null`. */
export function parseMailboxGone(value: unknown): MailboxGone | null {
  if (value === undefined || value === null) return null
  const parsed = mailboxGoneObjectSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

/**
 * The list filter for marked people, in ONE place so the name can follow the API
 * (FilterRecipientsDto.mailboxGone). It rides the same request as every other facet: the list, the KPI
 * metrics, the select-all across pages and the export all ask the question the table asks.
 */
export const MAILBOX_GONE_QUERY_PARAM = 'mailboxGone'
export const MAILBOX_GONE_QUERY_VALUE = 'true'
/** The table column / facet id (URL: `col_mailboxGone=true`). */
export const MAILBOX_GONE_FACET_ID = 'mailboxGone'

/**
 * Is this person marked AND not active? A marked person who is Active again (an admin reactivated them, the
 * API clears the mark in the same transaction) is an ordinary Active person: the screen shows the ordinary
 * switch, so a stale mark in a cached row never hides it.
 */
export function isMailboxGone(recipient: { status?: string | null; mailboxGone?: MailboxGone | null }): boolean {
  return !!recipient.mailboxGone && recipient.status !== 'Active'
}

/** The sentence "Mailbox no longer exists <where>, found by <feature> on <date>", one key per provider. */
export const MAILBOX_GONE_REASON_KEYS: Readonly<Record<MailboxGoneProvider, string>> = {
  google: 'recipients.mailboxGone.reason.google',
  microsoft: 'recipients.mailboxGone.reason.microsoft',
  smtp: 'recipients.mailboxGone.reason.smtp',
}
export const MAILBOX_GONE_REASON_UNKNOWN_KEY = 'recipients.mailboxGone.reason.unknown'

/** The feature that found it, as a noun phrase the sentence above takes ("Search & Remediate"). */
export const MAILBOX_GONE_SOURCE_KEYS: Readonly<Record<MailboxGoneSource, string>> = {
  connection_test: 'recipients.mailboxGone.source.connection_test',
  dwd_check: 'recipients.mailboxGone.source.dwd_check',
  snr: 'recipients.mailboxGone.source.snr',
  er_fetch: 'recipients.mailboxGone.source.er_fetch',
  gmail_watch: 'recipients.mailboxGone.source.gmail_watch',
  esa: 'recipients.mailboxGone.source.esa',
  delivery: 'recipients.mailboxGone.source.delivery',
  bounce: 'recipients.mailboxGone.source.bounce',
}
export const MAILBOX_GONE_SOURCE_UNKNOWN_KEY = 'recipients.mailboxGone.source.unknown'

/** The provider in words, for the confirm. */
export const MAILBOX_GONE_PROVIDER_KEYS: Readonly<Record<MailboxGoneProvider, string>> = {
  google: 'recipients.mailboxGone.provider.google',
  microsoft: 'recipients.mailboxGone.provider.microsoft',
  smtp: 'recipients.mailboxGone.provider.smtp',
}
export const MAILBOX_GONE_PROVIDER_UNKNOWN_KEY = 'recipients.mailboxGone.provider.unknown'

type Translate = (key: string, params?: Record<string, string | number>) => string

const own = <V,>(map: Readonly<Record<string, V>>, key: string): V | undefined =>
  Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined

/** The feature that found it, in the interface language; an unknown one reads generically, never the raw code. */
export function mailboxGoneSourceLabel(t: Translate, source: string): string {
  return t(own(MAILBOX_GONE_SOURCE_KEYS, source) ?? MAILBOX_GONE_SOURCE_UNKNOWN_KEY)
}

/** The provider in the interface language ("Google Workspace"). */
export function mailboxGoneProviderLabel(t: Translate, provider: string): string {
  return t(own(MAILBOX_GONE_PROVIDER_KEYS, provider) ?? MAILBOX_GONE_PROVIDER_UNKNOWN_KEY)
}

/**
 * The reason line: "Mailbox no longer exists in Google Workspace, found by Search & Remediate on 29 Oct 2026".
 * `date` is already formatted for the viewer (interface language, the viewer's device zone: an instant on an
 * admin screen, hfv3-timezone), because only the component knows the locale.
 */
export function mailboxGoneReason(t: Translate, mark: MailboxGone, date: string): string {
  const key = own(MAILBOX_GONE_REASON_KEYS, mark.provider) ?? MAILBOX_GONE_REASON_UNKNOWN_KEY
  return t(key, { feature: mailboxGoneSourceLabel(t, mark.source), date })
}

// ── Filters (the bulk Set Active asks first when the selection may hold marked people) ──

type FilterLike = Record<string, unknown> | null | undefined

/** The list is filtered to marked people only (`mailboxGone=true`): every person in it is marked. */
export function filterIsMarkedOnly(filters: FilterLike): boolean {
  const value = filters?.[MAILBOX_GONE_QUERY_PARAM]
  return value === MAILBOX_GONE_QUERY_VALUE || value === true
}

/**
 * Can this filter hold a marked person at all? A marked person is Inactive, so a status filter that is only Active
 * cannot; nor can `mailboxGone=false`. No status filter, or one that names anything else (or the "all" sentinel), can.
 */
export function filterExcludesMarkedPeople(filters: FilterLike): boolean {
  const value = filters?.[MAILBOX_GONE_QUERY_PARAM]
  if (value === 'false' || value === false) return true
  const raw = filters?.status
  const statuses = Array.isArray(raw) ? raw : typeof raw === 'string' && raw ? [raw] : []
  return statuses.length > 0 && statuses.every((status) => status === 'Active')
}
